import React, { ReactNode } from "react";

import { css } from "@emotion/core";
import { capitalize } from "lodash";

import { Text } from "@zapier/design-system";

import PaymentMethodImage from "@zapier/payment/esm/components/PaymentMethodImage";
import {
  CreditCardBrandCode,
  PaymentDetailsBank,
  PaymentDetailsCard,
  PaymentSource,
  PaymentSourceType,
} from "@zapier/payment/esm/types";
import { getCardDefinitionFromBrand } from "@zapier/payment/esm/utils";

const detailsCss = (isCompact = false) =>
  css({
    display: isCompact ? "block" : "flex",
    flexDirection: "column",
    lineHeight: "18px",
    marginLeft: isCompact ? 9 : 15,
    padding: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  });

const wrapperCss = {
  alignItems: "center",
  display: "flex",
};

type ChildrenProps = {
  descriptions: Descriptions;
};

type Props = {
  children?: (a: ChildrenProps) => ReactNode;
  isCompact?: boolean;
  /** Current payment source */
  paymentSource: PaymentSource;
};

type Descriptions = {
  imageType: string;
  firstLine: string;
  secondLine: string;
};

const getPaymentDescriptions = (
  paymentSource: PaymentSource,
  isCompact: boolean
): Descriptions => {
  const method = paymentSource.method;
  const hiddenCC = "••••";

  switch (method) {
    case PaymentSourceType.card: {
      const cardDetails: PaymentDetailsCard = paymentSource.details as any;
      const definition = getCardDefinitionFromBrand(
        cardDetails.brand as CreditCardBrandCode
      );
      const firstLinePrefix = `${definition.niceType} ending in`;
      return {
        imageType: definition.type,
        firstLine: `${isCompact ? hiddenCC : firstLinePrefix} ${
          cardDetails.last4
        }`,
        secondLine: `Expires ${cardDetails.expiry}`,
      };
    }

    case PaymentSourceType.bank: {
      const bankDetails: PaymentDetailsBank = paymentSource.details as any;
      const firstLineMiddleText = isCompact
        ? `${hiddenCC} ${bankDetails.last4}`
        : `${capitalize(bankDetails.accountType)}`;

      return {
        imageType: "ach",
        firstLine: `${bankDetails.bankName} ${firstLineMiddleText}`,
        secondLine: `Ending in ${bankDetails.last4}`,
      };
    }
    default:
      return {
        imageType: "unknown",
        firstLine: "",
        secondLine: "",
      };
  }
};

const renderDefaultInfo: React.FC<ChildrenProps> = ({ descriptions }) => {
  return (
    <React.Fragment>
      <Text fontWeight={700} type="paragraph3Bold">
        {descriptions.firstLine}
      </Text>
      <Text type="smallPrint1">{descriptions.secondLine}</Text>
    </React.Fragment>
  );
};

const renderCompactInfo: React.FC<ChildrenProps> = ({ descriptions }) => (
  <React.Fragment>
    <Text type="paragraph3">{descriptions.firstLine}</Text>
  </React.Fragment>
);

const PaymentInfo: React.FC<Props> = ({
  isCompact = false,
  children = isCompact ? renderCompactInfo : renderDefaultInfo,
  paymentSource,
}) => {
  const descriptions = getPaymentDescriptions(paymentSource, isCompact);
  return (
    <div css={wrapperCss}>
      <PaymentMethodImage
        imageType={descriptions.imageType}
        isSmall={isCompact}
      />
      <div css={detailsCss(isCompact)}>
        {children({
          descriptions,
        })}
      </div>
    </div>
  );
};

export default PaymentInfo;
